"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from "react";
import { useQueryParam } from "@/lib/routing";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Progress } from "@/app/components/ui/progress";
import { Badge } from "@/app/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/components/ui/tabs";
import { Textarea } from "@/app/components/ui/textarea";
import { Switch } from "@/app/components/ui/switch";
import { Separator } from "@/app/components/ui/separator";
import {
  Timer as TimerIcon,
  Play,
  Pause,
  RotateCcw,
  Settings,
  Coffee,
  Clock,
  Target,
  BarChart3,
  Activity,
  Brain,
  Heart,
  Star,
  FileText,
  Square,
  Save,
  X,
  Plus,
  Trash2,
  Check,
} from "lucide-react";
import { NumberStepper } from "@/app/components/ui/number-stepper";

/* ===========================
   Types
=========================== */

type TimerMode = "work" | "break" | "longBreak";
type TimerType = "pomodoro" | "custom" | "stopwatch";

interface TimerSettings {
  workSec: number;
  breakSec: number;
  longBreakSec: number;
  longBreakInterval: number;
  soundEnabled: boolean;
  theme: string;
  autoStartBreaks: boolean;
  autoStartWork: boolean;
  notifications: boolean;
}

interface Task {
  id: string;
  name: string;
  description: string;
  completed: boolean;
  pomodoros: number;
  createdAtISO: string;
  completedAtISO?: string;
}

interface Statistics {
  totalSessions: number;       // count of completed work sessions
  totalWorkMin: number;        // minutes
  totalBreakMin: number;       // minutes
  averageSessionLengthMin: number;
  longestStreak: number;
  currentStreak: number;
  productivityScore: number;   // 0..100
}

type BreakCategory = "physical" | "mental" | "social" | "creative";

interface BreakActivity {
  id: string;
  name: string;
  description: string;
  durationMin: number;
  category: BreakCategory;
  icon: React.ReactNode;
}

/* ===========================
   Utilities
=========================== */

const MIN_SEC = 1;
const MAX_WORK_SEC = 3 * 60 * 60; // cap reasonable session lengths (3h)
const MAX_BREAK_SEC = 2 * 60 * 60;

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const toHMS = (totalSec: number) => {
  const sec = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return { h, m, s };
};

const fromHMS = (h: number, m: number, s: number) =>
  Math.max(0, Math.floor(h) * 3600 + Math.floor(m) * 60 + Math.floor(s));

const fmt = (n: number) => n.toString().padStart(2, "0");

const formatTime = (seconds: number): string => {
  const { h, m, s } = toHMS(seconds);
  return h > 0 ? `${fmt(h)}:${fmt(m)}:${fmt(s)}` : `${fmt(m)}:${fmt(s)}`;
};

function safeParseInt(v: string, fallback = 0) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
}

/* ===========================
   Persistence Hook
=========================== */

function usePersistentState<T>(
  key: string,
  initial: T,
  reviver?: (raw: unknown) => T
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return initial;
      const parsed = JSON.parse(raw);
      return reviver ? reviver(parsed) : (parsed as T);
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // ignore quota errors
    }
  }, [key, state]);

  return [state, setState];
}

/* ===========================
   Duration Picker
=========================== */

interface DurationPickerProps {
  label: string;
  valueSec: number;
  onChange: (nextSec: number) => void;
  minSec?: number;
  maxSec?: number;
  disabled?: boolean;
  quick?: number[]; // quick preset buttons in minutes
}

const DurationPicker: React.FC<DurationPickerProps> = ({
  label,
  valueSec,
  onChange,
  minSec = MIN_SEC,
  maxSec = MAX_WORK_SEC,
  disabled,
  quick = [],
}) => {
  const { h, m, s } = toHMS(valueSec);

  const update = (nh: number, nm: number, ns: number) => {
    const next = clamp(fromHMS(nh, nm, ns), minSec, maxSec);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {label ? <Label>{label}</Label> : null}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberStepper
            ariaLabel="hours"
            value={h}
            onChange={(v) => update(v, m, s)}
            min={0}
            max={999}
            disabled={disabled}
          />
          <span className="opacity-60 shrink-0">:</span>
          <NumberStepper
            ariaLabel="minutes"
            value={m}
            onChange={(v) => update(h, v, s)}
            min={0}
            max={59}
            disabled={disabled}
          />
          <span className="opacity-60 shrink-0">:</span>
          <NumberStepper
            ariaLabel="seconds"
            value={s}
            onChange={(v) => update(h, m, v)}
            min={0}
            max={59}
            disabled={disabled}
          />
        </div>
        {quick.length > 0 && (
          <div className="flex flex-wrap gap-2 sm:ml-2">
            {quick.map((min) => (
              <Button
                key={min}
                type="button"
                size="sm"
                variant="outline"
                className="rounded-lg shrink-0"
                disabled={disabled}
                onClick={() => onChange(clamp(min * 60, minSec, maxSec))}
                title={`${min} minutes`}
              >
                {min}m
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ===========================
   Profiles
=========================== */

const PROFILES = {
  Focus: { workSec: 25 * 60, breakSec: 5 * 60, longBreakSec: 15 * 60, longBreakInterval: 4 },
  Flow: { workSec: 50 * 60, breakSec: 10 * 60, longBreakSec: 20 * 60, longBreakInterval: 3 },
  Deep: { workSec: 90 * 60, breakSec: 20 * 60, longBreakSec: 30 * 60, longBreakInterval: 2 },
} as const;
type ProfileKey = keyof typeof PROFILES;

/* ===========================
   Main Component
=========================== */

const TIMER_TABS = ["pomodoro", "custom", "stopwatch"] as const;

function PomodoroPageContent() {
  // Single-param state for `tab` with type safety
  const [timerType, setTimerType] = useQueryParam<TimerType>("tab", {
    parse: (raw) => (raw ?? "pomodoro") as TimerType,
    serialize: (v) => v ?? null,
    defaultValue: "pomodoro",
    replace: true, // keep history clean while switching tabs
  });
  const [mode, setMode] = useState<TimerMode>("work");
  const [isRunning, setIsRunning] = useState(false);
  const [completedSessions, setCompletedSessions] = useState(0);

  // Settings (persisted)
  const [settings, setSettings] = usePersistentState<TimerSettings>(
    "pomodoro-settings-v2",
    {
      workSec: 25 * 60,
      breakSec: 5 * 60,
      longBreakSec: 15 * 60,
      longBreakInterval: 4,
      soundEnabled: true,
      theme: "default",
      autoStartBreaks: false,
      autoStartWork: false,
      notifications: true,
    }
  );

  // Statistics (persisted)
  const [statistics, setStatistics] = usePersistentState<Statistics>(
    "pomodoro-statistics-v2",
    {
      totalSessions: 0,
      totalWorkMin: 0,
      totalBreakMin: 0,
      averageSessionLengthMin: 0,
      longestStreak: 0,
      currentStreak: 0,
      productivityScore: 0,
    }
  );

  // Tasks (persisted)
  const [tasks, setTasks] = usePersistentState<Task[]>(
    "pomodoro-tasks-v2",
    [],
    (raw) => {
      // ensure Dates are valid ISO strings; coerce if coming from older shape
      const list = Array.isArray(raw) ? raw : [];
      return list.map((t: any) => ({
        id: String(t.id ?? Date.now().toString()),
        name: String(t.name ?? ""),
        description: String(t.description ?? ""),
        completed: Boolean(t.completed ?? false),
        pomodoros: Number.isFinite(t.pomodoros) ? t.pomodoros : 0,
        createdAtISO: typeof t.createdAtISO === "string" ? t.createdAtISO : new Date().toISOString(),
        completedAtISO: typeof t.completedAtISO === "string" ? t.completedAtISO : undefined,
      })) as Task[];
    }
  );

  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const currentTask = useMemo(
    () => tasks.find((t) => t.id === currentTaskId) ?? null,
    [tasks, currentTaskId]
  );


  // Core timer state
  const [remainingSec, setRemainingSec] = useState<number>(settings.workSec);
  const [customSec, setCustomSec] = useState<number>(25 * 60); // for Custom tab
  const targetMsRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const baseStopwatchSecRef = useRef<number>(0); // accumulated when paused
  const stopwatchStartMsRef = useRef<number | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  /* ---------- Derived helpers ---------- */

  const totalForMode = useMemo(() => {
    if (timerType === "custom") return customSec;
    if (timerType === "stopwatch") return 0;
    switch (mode) {
      case "work":
        return settings.workSec;
      case "break":
        return settings.breakSec;
      case "longBreak":
        return settings.longBreakSec;
    }
  }, [timerType, customSec, mode, settings]);

  const progressPct = useMemo(() => {
    if (timerType === "stopwatch") return 0;
    const spent = Math.max(0, totalForMode - remainingSec);
    return totalForMode > 0 ? (spent / totalForMode) * 100 : 0;
  }, [timerType, totalForMode, remainingSec]);

  const modeColor = useMemo(() => {
    if (timerType === "stopwatch") return "#7c3aed";
    if (mode === "work") return "#dc2626";
    if (mode === "break") return "#16a34a";
    return "#2563eb";
  }, [timerType, mode]);

  const modeTitle = useMemo(() => {
    if (timerType === "stopwatch") return "Stopwatch";
    if (timerType === "custom") return "Custom Timer";
    if (mode === "work") return "Work Session";
    if (mode === "break") return "Short Break";
    return "Long Break";
  }, [timerType, mode]);

  /* ---------- Timer engine (drift-resistant) ---------- */

  const clearTick = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startTick = useCallback(() => {
    clearTick();
    intervalRef.current = setInterval(() => {
      if (timerType === "stopwatch") {
        // elapsed = base + (now - start)
        const now = Date.now();
        const start = stopwatchStartMsRef.current;
        const base = baseStopwatchSecRef.current;
        if (start != null) {
          const elapsedSec = base + Math.floor((now - start) / 1000);
          setRemainingSec(() => elapsedSec); // here remainingSec is used as elapsed for stopwatch
        }
        return;
      }

      // countdown
      const target = targetMsRef.current;
      if (target == null) return;

      const now = Date.now();
      const secLeft = Math.max(0, Math.ceil((target - now) / 1000));
      setRemainingSec(secLeft);

      if (secLeft <= 0) {
        clearTick();
        handleTimerComplete();
      }
    }, 250);
  }, [timerType]);

  /* ---------- Lifecycle ---------- */

  // Keep remainingSec aligned when switching modes or settings while not running
  useEffect(() => {
    if (isRunning) return;
    if (timerType === "pomodoro") {
      if (mode === "work") setRemainingSec(settings.workSec);
      if (mode === "break") setRemainingSec(settings.breakSec);
      if (mode === "longBreak") setRemainingSec(settings.longBreakSec);
    }
  }, [settings, mode, timerType, isRunning]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        isRunning ? pauseTimer() : startTimer();
      } else if (e.key.toLowerCase() === "r") {
        e.preventDefault();
        resetTimer();
      } else if (e.key.toLowerCase() === "s") {
        e.preventDefault();
        skipTimer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isRunning]);

  useEffect(() => {
    return () => clearTick();
  }, []);

  /* ---------- Notifications ---------- */

  const requestNotificationsIfNeeded = useCallback(() => {
    if (!("Notification" in window)) return;
    if (!settings.notifications) return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => void 0);
    }
  }, [settings.notifications]);

  useEffect(() => {
    requestNotificationsIfNeeded();
  }, [requestNotificationsIfNeeded]);

  /* ---------- Actions ---------- */

  const startTimer = useCallback(() => {
    setIsRunning(true);

    if (timerType === "stopwatch") {
      stopwatchStartMsRef.current = Date.now();
      startTick();
      return;
    }

    // countdown
    targetMsRef.current = Date.now() + remainingSec * 1000;
    startTick();
  }, [remainingSec, startTick, timerType]);

  const pauseTimer = useCallback(() => {
    setIsRunning(false);
    clearTick();

    if (timerType === "stopwatch") {
      // accumulate elapsed into base
      const start = stopwatchStartMsRef.current;
      if (start != null) {
        baseStopwatchSecRef.current =
          baseStopwatchSecRef.current + Math.floor((Date.now() - start) / 1000);
        stopwatchStartMsRef.current = null;
      }
      return;
    }

    // countdown: recompute remaining against target and freeze
    const target = targetMsRef.current;
    if (target != null) {
      const secLeft = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setRemainingSec(secLeft);
      targetMsRef.current = null;
    }
  }, [timerType]);

  const resetTimer = useCallback(() => {
    setIsRunning(false);
    clearTick();

    if (timerType === "stopwatch") {
      baseStopwatchSecRef.current = 0;
      stopwatchStartMsRef.current = null;
      setRemainingSec(0);
      return;
    }

    if (timerType === "custom") {
      setRemainingSec(customSec);
      return;
    }

    // pomodoro
    if (mode === "work") setRemainingSec(settings.workSec);
    if (mode === "break") setRemainingSec(settings.breakSec);
    if (mode === "longBreak") setRemainingSec(settings.longBreakSec);
  }, [customSec, mode, settings, timerType]);

  const skipTimer = useCallback(() => {
    if (timerType === "stopwatch") return; // skipping stopwatch doesn't apply
    handleTimerComplete();
  }, [timerType]);

  const playDing = () => {
    if (settings.soundEnabled && audioRef.current) {
      audioRef.current.play().catch(() => void 0);
    }
  };

  const notify = (body: string) => {
    if (!settings.notifications) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    new Notification("Pomodoro Timer", { body, icon: "/favicon.ico" });
  };

  const handleTimerComplete = useCallback(() => {
    setIsRunning(false);
    clearTick();
    playDing();

    if (timerType === "custom") {
      notify("Custom timer finished.");
      return;
    }
    if (timerType === "stopwatch") return;

    if (mode === "work") {
      setCompletedSessions((c) => c + 1);
      // stats
      setStatistics((prev) => {
        const next = { ...prev };
        next.totalSessions += 1;
        next.totalWorkMin += Math.round(settings.workSec / 60);
        next.currentStreak += 1;
        next.longestStreak = Math.max(next.longestStreak, next.currentStreak);
        next.averageSessionLengthMin =
          next.totalWorkMin / Math.max(1, next.totalSessions);
        next.productivityScore = Math.min(100, (next.totalWorkMin / 60) * 2);
        return next;
      });

      if (currentTask) {
        setTasks((prev) =>
          prev.map((t) =>
            t.id === currentTask.id ? { ...t, pomodoros: t.pomodoros + 1 } : t
          )
        );
      }

      // decide next mode
      const nextIsLong =
        (completedSessions + 1) % settings.longBreakInterval === 0;
      const nextMode: TimerMode = nextIsLong ? "longBreak" : "break";
      setMode(nextMode);
      setRemainingSec(nextIsLong ? settings.longBreakSec : settings.breakSec);

      notify(nextIsLong ? "Work done. Long break time!" : "Work done. Break time!");

      if (settings.autoStartBreaks) {
        setTimeout(() => {
          setIsRunning(true);
          targetMsRef.current = Date.now() + (nextIsLong ? settings.longBreakSec : settings.breakSec) * 1000;
          startTick();
        }, 350);
      }
    } else {
      // from break to work
      setMode("work");
      setRemainingSec(settings.workSec);

      setStatistics((prev) => {
        const next = { ...prev };
        next.totalBreakMin += Math.round(
          (mode === "longBreak" ? settings.longBreakSec : settings.breakSec) / 60
        );
        next.currentStreak = 0;
        next.averageSessionLengthMin =
          next.totalWorkMin / Math.max(1, next.totalSessions);
        next.productivityScore = Math.min(100, (next.totalWorkMin / 60) * 2);
        return next;
      });

      notify("Break finished. Back to work!");

      if (settings.autoStartWork) {
        setTimeout(() => {
          setIsRunning(true);
          targetMsRef.current = Date.now() + settings.workSec * 1000;
          startTick();
        }, 350);
      }
    }
  }, [
    timerType,
    mode,
    settings,
    completedSessions,
    currentTask,
    setStatistics,
    setTasks,
    startTick,
  ]);

  /* ---------- Tasks ---------- */

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newTask, setNewTask] = useState({ name: "", description: "" });

  const addTask = () => {
    const name = newTask.name.trim();
    if (!name) return;
    const task: Task = {
      id: `${Date.now()}`,
      name,
      description: newTask.description.trim(),
      completed: false,
      pomodoros: 0,
      createdAtISO: new Date().toISOString(),
    };
    setTasks((prev) => [task, ...prev]);
    setNewTask({ name: "", description: "" });
    setShowTaskForm(false);
  };

  const completeTask = (id: string) => {
    setTasks((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, completed: true, completedAtISO: new Date().toISOString() } : t
      )
    );
    if (currentTaskId === id) setCurrentTaskId(null);
  };

  const deleteTask = (id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    if (currentTaskId === id) setCurrentTaskId(null);
  };

  /* ---------- Break activities ---------- */

  const breakActivities: BreakActivity[] = [
    {
      id: "1",
      name: "Stretch & Move",
      description: "Light stretching or a short walk.",
      durationMin: 5,
      category: "physical",
      icon: <Activity className="h-4 w-4" />,
    },
    {
      id: "2",
      name: "Deep Breathing",
      description: "Box breathing for relaxation.",
      durationMin: 3,
      category: "mental",
      icon: <Heart className="h-4 w-4" />,
    },
    {
      id: "3",
      name: "Hydrate",
      description: "Drink water. Your brain is 75% squishy water anyway.",
      durationMin: 2,
      category: "physical",
      icon: <Coffee className="h-4 w-4" />,
    },
    {
      id: "4",
      name: "Quick Meditation",
      description: "2–5 min attention reset.",
      durationMin: 5,
      category: "mental",
      icon: <Brain className="h-4 w-4" />,
    },
    {
      id: "5",
      name: "Creative Break",
      description: "Doodle, riff, or noodle.",
      durationMin: 10,
      category: "creative",
      icon: <Star className="h-4 w-4" />,
    },
  ];

  /* ---------- UI helpers ---------- */

  const applyProfile = (key: ProfileKey, applyToCurrent = true) => {
    const p = PROFILES[key];
    setSettings((prev) => ({
      ...prev,
      workSec: p.workSec,
      breakSec: p.breakSec,
      longBreakSec: p.longBreakSec,
      longBreakInterval: p.longBreakInterval,
    }));
    // Optionally affect current countdown now:
    if (applyToCurrent && !isRunning && timerType === "pomodoro") {
      if (mode === "work") setRemainingSec(p.workSec);
      if (mode === "break") setRemainingSec(p.breakSec);
      if (mode === "longBreak") setRemainingSec(p.longBreakSec);
    }
  };

  const applySettingsToCurrent = () => {
    if (isRunning) return;
    if (timerType === "custom") {
      setRemainingSec(customSec);
      return;
    }
    if (timerType === "pomodoro") {
      if (mode === "work") setRemainingSec(settings.workSec);
      if (mode === "break") setRemainingSec(settings.breakSec);
      if (mode === "longBreak") setRemainingSec(settings.longBreakSec);
    }
  };

  /* ===========================
     Render
  =========================== */

  return (
    <div className="container mx-auto px-4 py-8 overflow-x-hidden">
      <audio ref={audioRef} preload="auto">
        <source src="/sounds/new-notification-020-352772.mp3" type="audio/mpeg" />
      </audio>

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Enhanced Timer</h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Pomodoro, Custom Timer, Stopwatch, and Task Management
          </p>
        </div>

        <Tabs value={timerType} onValueChange={(value) => setTimerType(value as TimerType)} className="w-full">
          <TabsList className="flex w-full flex-wrap gap-2 p-2 mb-8 min-h-12 rounded-xl bg-muted/60 [&>button]:flex-1 [&>button]:min-w-0 sm:[&>button]:flex-initial">
            <TabsTrigger value="pomodoro" className="flex items-center justify-center gap-2 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm px-4 py-2">
              <TimerIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">Pomodoro</span>
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex items-center justify-center gap-2 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm px-4 py-2">
              <Target className="h-4 w-4 shrink-0" />
              <span className="truncate">Custom</span>
            </TabsTrigger>
            <TabsTrigger value="stopwatch" className="flex items-center justify-center gap-2 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm px-4 py-2">
              <Clock className="h-4 w-4 shrink-0" />
              <span className="truncate">Stopwatch</span>
            </TabsTrigger>
          </TabsList>

          {/* ======= Pomodoro ======= */}
          <TabsContent value="pomodoro" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="overflow-hidden border-2 transition-colors duration-300" style={{ borderColor: isRunning ? `${modeColor}20` : undefined }}>
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="flex items-center justify-center gap-2 flex-wrap">
                      {timerType === "stopwatch" ? (
                        <Clock className="h-6 w-6" style={{ color: modeColor }} />
                      ) : mode === "work" ? (
                        <TimerIcon className="h-6 w-6" style={{ color: modeColor }} />
                      ) : (
                        <Coffee className="h-6 w-6" style={{ color: modeColor }} />
                      )}
                      <span>{modeTitle}</span>
                      <Badge variant="secondary" className="font-medium">{completedSessions} completed</Badge>
                    </CardTitle>
                    <CardDescription>
                      {mode === "work" ? "Stay focused and productive" : "Take a well-deserved break"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex flex-col items-center">
                      <div className="relative inline-flex items-center justify-center">
                        <svg className="w-56 h-56 -rotate-90" viewBox="0 0 120 120" aria-hidden>
                          <circle cx="60" cy="60" r="54" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/30" />
                          {timerType !== "stopwatch" && (
                            <circle
                              cx="60"
                              cy="60"
                              r="54"
                              fill="none"
                              stroke={modeColor}
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={339.292}
                              strokeDashoffset={339.292 - (progressPct / 100) * 339.292}
                              className="transition-all duration-500 ease-out"
                            />
                          )}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span
                            className="text-5xl sm:text-6xl font-mono font-bold tabular-nums tracking-tight"
                            style={{ color: modeColor }}
                          >
                            {timerType === "stopwatch"
                              ? formatTime(remainingSec)
                              : formatTime(remainingSec)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {timerType !== "stopwatch" && (
                      <>
                        <div className="space-y-2">
                          <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500 ease-out"
                              style={{ width: `${progressPct}%`, backgroundColor: modeColor }}
                            />
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Progress</span>
                            <span className="font-medium" style={{ color: modeColor }}>{Math.round(progressPct)}%</span>
                          </div>
                        </div>

                        <div className="text-center space-y-1 py-2 px-4 rounded-lg bg-muted/40">
                          <p className="text-sm font-medium">
                            Session {completedSessions + 1} of {settings.longBreakInterval}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {mode === "work"
                              ? `Next: ${(completedSessions + 1) % settings.longBreakInterval === 0 ? "Long Break" : "Short Break"}`
                              : "Next: Work Session"}
                          </p>
                        </div>
                      </>
                    )}

                    <div className="flex flex-wrap justify-center gap-3">
                      {!isRunning ? (
                        <Button onClick={startTimer} size="lg" className="min-w-[120px] rounded-xl bg-primary hover:bg-primary/90">
                          <Play className="h-5 w-5 mr-2" />
                          Start
                        </Button>
                      ) : (
                        <Button onClick={pauseTimer} size="lg" variant="outline" className="min-w-[120px] rounded-xl">
                          <Pause className="h-5 w-5 mr-2" />
                          Pause
                        </Button>
                      )}
                      <Button onClick={resetTimer} size="lg" variant="outline" className="rounded-xl">
                        <RotateCcw className="h-5 w-5 mr-2" />
                        Reset
                      </Button>
                      {timerType !== "stopwatch" && (
                        <Button onClick={skipTimer} size="lg" variant="outline" className="rounded-xl">
                          Skip
                        </Button>
                      )}
                    </div>
                    <p className="text-center text-xs text-muted-foreground">
                      <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">Space</kbd> Start/Pause
                      {" · "}
                      <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">R</kbd> Reset
                      {" · "}
                      <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">S</kbd> Skip
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Settings */}
              <div className="space-y-6 min-w-0">
                <Card className="overflow-hidden">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Settings className="h-5 w-5 shrink-0 text-muted-foreground" />
                      Timer Settings
                    </CardTitle>
                    <CardDescription>Full length control with HH:MM:SS</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 min-w-0">
                    {/* Profiles */}
                    <div className="flex flex-wrap gap-2">
                      {(Object.keys(PROFILES) as ProfileKey[]).map((k) => (
                        <Button
                          key={k}
                          variant="outline"
                          size="sm"
                          className="rounded-lg transition-colors"
                          onClick={() => applyProfile(k, true)}
                          title={`${k} profile`}
                        >
                          {k}
                        </Button>
                      ))}
                    </div>

                    <Separator className="my-4" />

                    {/* Work */}
                    <DurationPicker
                      label="Work Duration"
                      valueSec={settings.workSec}
                      onChange={(sec) =>
                        setSettings((p) => ({ ...p, workSec: clamp(sec, MIN_SEC, MAX_WORK_SEC) }))
                      }
                      quick={[15, 20, 25, 30, 45, 50, 60, 90]}
                      disabled={isRunning}
                    />

                    {/* Short Break */}
                    <DurationPicker
                      label="Short Break Duration"
                      valueSec={settings.breakSec}
                      onChange={(sec) =>
                        setSettings((p) => ({ ...p, breakSec: clamp(sec, MIN_SEC, MAX_BREAK_SEC) }))
                      }
                      quick={[3, 5, 7, 10, 15]}
                      disabled={isRunning}
                      maxSec={MAX_BREAK_SEC}
                    />

                    {/* Long Break */}
                    <DurationPicker
                      label="Long Break Duration"
                      valueSec={settings.longBreakSec}
                      onChange={(sec) =>
                        setSettings((p) => ({ ...p, longBreakSec: clamp(sec, MIN_SEC, MAX_BREAK_SEC) }))
                      }
                      quick={[10, 15, 20, 25, 30]}
                      disabled={isRunning}
                      maxSec={MAX_BREAK_SEC}
                    />

                    <div className="space-y-2 min-w-0">
                      <Label htmlFor="longBreakInterval">Long Break Interval (sessions)</Label>
                      <Input
                        id="longBreakInterval"
                        type="number"
                        min={2}
                        max={10}
                        className="rounded-lg w-full max-w-[120px]"
                        value={settings.longBreakInterval}
                        disabled={isRunning}
                        onChange={(e) =>
                          setSettings((p) => ({
                            ...p,
                            longBreakInterval: clamp(safeParseInt(e.target.value, p.longBreakInterval), 2, 10),
                          }))
                        }
                      />
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full rounded-lg shrink-0"
                      disabled={isRunning}
                      onClick={applySettingsToCurrent}
                      title="Apply current durations to this session immediately"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Apply To Current Session
                    </Button>

                    <Separator className="my-4" />

                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-4 rounded-lg py-2 px-3 bg-muted/30 min-w-0">
                        <Label htmlFor="soundEnabled" className="cursor-pointer shrink-0 text-sm sm:text-base">Sound Notifications</Label>
                        <Switch
                          id="soundEnabled"
                          checked={settings.soundEnabled}
                          onCheckedChange={(checked) =>
                            setSettings((p) => ({ ...p, soundEnabled: checked }))
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-lg py-2 px-3 bg-muted/30 min-w-0">
                        <Label htmlFor="notifications" className="cursor-pointer shrink-0 text-sm sm:text-base">Browser Notifications</Label>
                        <Switch
                          id="notifications"
                          checked={settings.notifications}
                          onCheckedChange={(checked) =>
                            setSettings((p) => ({ ...p, notifications: checked }))
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-lg py-2 px-3 bg-muted/30 min-w-0">
                        <Label htmlFor="autoStartBreaks" className="cursor-pointer shrink-0 text-sm sm:text-base">Auto-start Breaks</Label>
                        <Switch
                          id="autoStartBreaks"
                          checked={settings.autoStartBreaks}
                          onCheckedChange={(checked) =>
                            setSettings((p) => ({ ...p, autoStartBreaks: checked }))
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-lg py-2 px-3 bg-muted/30 min-w-0">
                        <Label htmlFor="autoStartWork" className="cursor-pointer shrink-0 text-sm sm:text-base">Auto-start Work</Label>
                        <Switch
                          id="autoStartWork"
                          checked={settings.autoStartWork}
                          onCheckedChange={(checked) =>
                            setSettings((p) => ({ ...p, autoStartWork: checked }))
                          }
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-muted-foreground" />
                      Statistics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <StatRow label="Total Sessions" right={<Badge variant="outline" className="font-mono">{statistics.totalSessions}</Badge>} />
                    <StatRow
                      label="Total Work Time"
                      right={
                        <span className="text-sm font-medium tabular-nums">
                          {Math.floor(statistics.totalWorkMin / 60)}h {statistics.totalWorkMin % 60}m
                        </span>
                      }
                    />
                    <StatRow label="Current Streak" right={<Badge variant="secondary" className="font-mono">{statistics.currentStreak}</Badge>} />
                    <StatRow label="Longest Streak" right={<Badge variant="secondary" className="font-mono">{statistics.longestStreak}</Badge>} />
                    <div className="pt-2 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Productivity Score</span>
                        <span className="font-medium">{Math.round(statistics.productivityScore)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-500"
                          style={{ width: `${Math.min(100, statistics.productivityScore)}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Task Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    Task Management
                  </div>
                  <Button onClick={() => setShowTaskForm((s) => !s)} size="sm" className="rounded-lg">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showTaskForm && (
                  <div className="space-y-4 mb-6 p-4 rounded-xl border bg-muted/20">
                    <div className="space-y-2">
                      <Label htmlFor="taskName">Task Name</Label>
                      <Input
                        id="taskName"
                        placeholder="Enter task name"
                        className="rounded-lg"
                        value={newTask.name}
                        onChange={(e) => setNewTask((p) => ({ ...p, name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="taskDescription">Description (optional)</Label>
                      <Textarea
                        id="taskDescription"
                        placeholder="Enter task description"
                        className="rounded-lg min-h-[80px]"
                        value={newTask.description}
                        onChange={(e) => setNewTask((p) => ({ ...p, description: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={addTask} size="sm" className="rounded-lg">
                        <Save className="h-4 w-4 mr-2" />
                        Save Task
                      </Button>
                      <Button onClick={() => setShowTaskForm(false)} size="sm" variant="outline" className="rounded-lg">
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {tasks.filter((t) => !t.completed).map((task) => (
                    <div
                      key={task.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border transition-colors ${
                        currentTaskId === task.id ? "border-primary/50 bg-primary/5" : "hover:bg-muted/30"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Button variant="ghost" size="icon" className="shrink-0 rounded-lg" onClick={() => completeTask(task.id)} aria-label="Complete task">
                          <Square className="h-4 w-4" />
                        </Button>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{task.name}</div>
                          {task.description && (
                            <div className="text-sm text-muted-foreground line-clamp-2">{task.description}</div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <span className="font-mono font-medium text-foreground">{task.pomodoros}</span>
                              pomodoros
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        {currentTaskId === task.id && (
                          <Badge variant="default" className="rounded-md">Active</Badge>
                        )}
                        <Button
                          variant={currentTaskId === task.id ? "secondary" : "outline"}
                          size="sm"
                          className="rounded-lg"
                          onClick={() =>
                            setCurrentTaskId((id) => (id === task.id ? null : task.id))
                          }
                        >
                          {currentTaskId === task.id ? "Stop" : "Start"}
                        </Button>
                        <Button variant="ghost" size="icon" className="shrink-0 rounded-lg text-muted-foreground hover:text-destructive" onClick={() => deleteTask(task.id)} aria-label="Delete task">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {tasks.filter((t) => !t.completed).length === 0 && (
                    <div className="text-center py-12 text-muted-foreground rounded-xl border border-dashed">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
                      <p className="font-medium text-foreground/80">No active tasks</p>
                      <p className="text-sm mt-1">Add a task to track pomodoros</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Break Activities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-muted-foreground" />
                  Break Activities
                </CardTitle>
                <CardDescription>Suggested activities for your break time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {breakActivities.map((a) => (
                    <div
                      key={a.id}
                      className="p-4 rounded-xl border bg-card hover:bg-muted/40 hover:border-muted-foreground/20 transition-all duration-200"
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          {a.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium">{a.name}</h4>
                            <Badge variant="secondary" className="rounded-md font-mono text-xs">
                              {a.durationMin}m
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{a.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ======= Custom ======= */}
          <TabsContent value="custom" className="space-y-6">
            <div className="max-w-2xl mx-auto min-w-0">
              <Card className="overflow-hidden">
                <CardContent className="space-y-8 p-4 sm:p-8">
                  <div className="text-center space-y-4">
                    <div className="text-6xl sm:text-7xl font-mono font-bold tabular-nums text-foreground tracking-tight">
                      {formatTime(remainingSec)}
                    </div>
                    {timerType === "custom" && remainingSec !== customSec && (
                      <div className="text-sm text-muted-foreground">
                        Set: {formatTime(customSec)}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <DurationPicker
                      label=""
                      valueSec={customSec}
                      onChange={setCustomSec}
                      minSec={MIN_SEC}
                      maxSec={MAX_WORK_SEC}
                      quick={[1, 2, 5, 10, 15, 20, 30, 60, 90]}
                    />
                  </div>

                  <div className="flex justify-center items-center gap-3 flex-wrap">
                    {!isRunning ? (
                      <Button
                        onClick={() => {
                          if (remainingSec !== customSec) {
                            setRemainingSec(customSec);
                            setMode("work");
                          }
                          startTimer();
                        }}
                        size="lg"
                        className="min-w-[120px] rounded-xl"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Start
                      </Button>
                    ) : (
                      <Button
                        onClick={pauseTimer}
                        size="lg"
                        variant="outline"
                        className="min-w-[120px] rounded-xl"
                      >
                        <Pause className="h-4 w-4 mr-2" />
                        Pause
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        resetTimer();
                        setRemainingSec(customSec);
                      }}
                      size="lg"
                      variant="outline"
                      className="min-w-[100px] rounded-xl"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ======= Stopwatch ======= */}
          <TabsContent value="stopwatch" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#7c3aed]" />
                    Stopwatch
                  </CardTitle>
                  <CardDescription>Track elapsed time</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <div className="text-5xl sm:text-6xl font-mono font-bold tabular-nums tracking-tight" style={{ color: "#7c3aed" }}>
                      {formatTime(remainingSec)}
                    </div>
                  </div>

                  <div className="flex justify-center gap-3 flex-wrap">
                    {!isRunning ? (
                      <Button
                        onClick={() => {
                          setIsRunning(true);
                          stopwatchStartMsRef.current = Date.now();
                          startTick();
                        }}
                        size="lg"
                        className="rounded-xl min-w-[120px]"
                      >
                        <Play className="h-5 w-5 mr-2" />
                        Start
                      </Button>
                    ) : (
                      <Button onClick={pauseTimer} size="lg" variant="outline" className="rounded-xl min-w-[120px]">
                        <Pause className="h-5 w-5 mr-2" />
                        Pause
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setIsRunning(false);
                        clearTick();
                        baseStopwatchSecRef.current = 0;
                        stopwatchStartMsRef.current = null;
                        setRemainingSec(0);
                      }}
                      size="lg"
                      variant="outline"
                      className="rounded-xl"
                    >
                      <RotateCcw className="h-5 w-5 mr-2" />
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Stopwatch Info</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <StatRow label="Current Time" right={<span className="font-medium font-mono tabular-nums">{formatTime(remainingSec)}</span>} />
                    <StatRow
                      label="Status"
                      right={<Badge variant={isRunning ? "default" : "secondary"} className="rounded-md">{isRunning ? "Running" : "Stopped"}</Badge>}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default function PomodoroPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
      <PomodoroPageContent />
    </Suspense>
  );
}

/* ===========================
   Small UI bits
=========================== */

const StatRow: React.FC<{ label: string; right: React.ReactNode }> = ({ label, right }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm">{label}</span>
    {right}
  </div>
);
